import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {UserPlus} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {useServices} from '@/services/context'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {PartyControllerPartiesEndpoint} from '@/types/endpoint-contracts'
import {omitContractFilters} from '@/types/endpoint-contract'
import {PartyCreateRequestFields} from '@/types/party'
import {useI18n} from '@/i18n'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {SortableHeader} from '@/components/ui/sortable-header'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON, type ConceptKey} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {formatDateOnly} from '@/lib/date-utils'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'

/**
 * Parties list page — Phase B.1 flagship for Unified Search + Filter (spec
 * §6 Phase B.1 / issue 342.04). Replaces the legacy SearchInput + inline
 * partyType Select with a single {@link UnifiedSearchBar} driven by the
 * {@link PartyControllerPartiesEndpoint} contract.
 *
 * Spec invariants this component upholds:
 *   • Zero implicit default filters — an empty URL shows every party.
 *   • One request per submit / chip removal — the text input only fires a
 *     network round-trip when the user presses Enter, via
 *     `setSearchAndFilters({q: ...})`.
 *   • Admin-only `parentPartyId` filter hidden from non-admin users by
 *     {@link UnifiedSearchBar} (§4.3 admin-only + SEC-05).
 *
 * Chrome DevTools verification and the full E2E matrix live in the
 * accompanying issue file.
 */
export interface PromotedListTheme {
    title: string
    icon?: ConceptKey
    createLabel?: string
    countLabel?: (params: {count: number}) => string
}

export interface PartiesProps {
    /**
     * When set, the page hides the matching filter from the search bar and
     * pins it server-side — used by the promoted by-type route so the user
     * never sees or removes the type filter (issue 301).
     */
    lockedType?: string
    /**
     * Optional re-themed page chrome (title, icon, count noun, Create button)
     * for the promoted by-type route. Falls through to the standard Parties
     * chrome when absent.
     */
    themedAs?: PromotedListTheme
}

export function Parties({lockedType, themedAs}: PartiesProps = {}) {
    const i18n = useI18n()
    const services = useServices()
    const {partyTypes} = useAppConfig()
    const {hasPermission} = useAuth()
    const navigate = useNavigate()

    const effectiveContract = useMemo(
        () => lockedType
            ? omitContractFilters(PartyControllerPartiesEndpoint, [PartyCreateRequestFields.partyType])
            : PartyControllerPartiesEndpoint,
        [lockedType],
    )

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        sortBy, sortDir, setSort,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: PartyControllerPartiesEndpoint,
        sortable: true,
        defaultSortBy: PartyControllerPartiesEndpoint.sort.DISPLAY_NAME,
        defaultSortDir: SortDir.ASC,
    })

    const requestParams = useMemo(() => {
        const params = Object.fromEntries(new URLSearchParams(queryString))
        if (lockedType) params[PartyCreateRequestFields.partyType] = lockedType
        return params
    }, [queryString, lockedType])

    const {data: partiesData} = useQuery({
        queryKey: ['parties', queryString, page, pageSize, sortBy, sortDir, lockedType],
        queryFn: () => services.parties.list(requestParams),
    })

    const parties = partiesData?.content || []
    const totalItems = partiesData?.totalElements || 0
    const totalPages = partiesData?.totalPages || 0

    const hasFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const headerIcon = themedAs?.icon ? CONCEPT_ICON[themedAs.icon] : CONCEPT_ICON.party
    const headerTitle = themedAs?.title ?? i18n.parties.allParties
    const countLabel = themedAs?.countLabel
        ? themedAs.countLabel({count: totalItems})
        : i18n.parties.partyCount({count: totalItems})
    const createLabel = themedAs?.createLabel ?? i18n.onboarding.actions.newUser

    return (
        <PageLayout
            variant="list"
            icon={headerIcon}
            title={headerTitle}
            count={totalItems}
            description={`${countLabel}${hasFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="parties-list"
            actions={hasPermission(permissions.onboarding.create) ? (
                <Button
                    size="sm"
                    onClick={() => navigate(lockedType
                        ? routes.identityManagement.parties.createByType(lockedType)
                        : routes.identityManagement.onboarding.create())}
                    data-testid="btn-new-user"
                >
                    <Icon icon={UserPlus} className="mr-1"/>
                    {createLabel}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={effectiveContract}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    attributeEntityType="party"
                    placeholder={i18n.parties.filterPlaceholder}
                    lockedDiscriminatorValues={lockedType ? [lockedType] : undefined}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">
                            <SortableHeader
                                label={i18n.common.name}
                                sortKey={PartyControllerPartiesEndpoint.sort.DISPLAY_NAME}
                                currentSortBy={sortBy}
                                currentSortDir={sortDir}
                                onSort={setSort}
                            />
                        </TableHead>
                        <TableHead data-testid="col-type">
                            <SortableHeader
                                label={i18n.common.type}
                                sortKey={PartyControllerPartiesEndpoint.sort.PARTY_TYPE}
                                currentSortBy={sortBy}
                                currentSortDir={sortDir}
                                onSort={setSort}
                            />
                        </TableHead>
                        <TableHead data-testid="col-created-at">
                            <SortableHeader
                                label={i18n.common.createdAt}
                                sortKey={PartyControllerPartiesEndpoint.sort.CREATED_AT}
                                currentSortBy={sortBy}
                                currentSortDir={sortDir}
                                onSort={setSort}
                            />
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {parties.map((party) => (
                        <ClickableTableRow key={party.id} to={routes.identityManagement.parties.detail(party.id)} data-testid="party-row">
                            <TableCell className="font-medium">
                                <TextLink
                                    to={routes.identityManagement.parties.detail(party.id)}
                                    testId={`link-party-detail-${party.id}`}
                                    stopPropagation
                                >
                                    {party.derivedDisplayName}
                                </TextLink>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {labelOf(partyTypes, party.partyType)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <MutedText>{formatDateOnly(party.createdAt)}</MutedText>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {parties.length === 0 && (
                        <TableEmptyState
                            colSpan={3}
                            hasFilters={hasFilters}
                            matchMessage={i18n.parties.noPartiesMatch}
                            emptyMessage={i18n.parties.noPartiesFound}
                        />
                    )}
                </TableBody>
            </Table>

        </PageLayout>
    )
}
